import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import type { RootBlockModel } from '@blocksuite/affine-model';
import { EditPropsStore } from '@blocksuite/affine-shared/services';
import { WidgetComponent, WidgetViewExtension } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { effect } from '@preact/signals-core';
import { nothing } from 'lit';
import { literal, unsafeStatic } from 'lit/static-html.js';

import { PresentTool } from '../present-tool';

/**
 * CSS class applied to edgeless root when black background is active.
 * This changes the canvas background to black instead of using a box-shadow hack.
 */
const BLACK_BACKGROUND_CLASS = 'presentation-black-background';

export const EDGELESS_NAVIGATOR_BLACK_BACKGROUND_WIDGET =
  'edgeless-navigator-black-background';

export class EdgelessNavigatorBlackBackgroundWidget extends WidgetComponent<RootBlockModel> {
  private _blackBackground = false;
  private _edgelessRoot: Element | null = null;

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  private get _slots() {
    return this.std.get(EdgelessLegacySlotIdentifier);
  }

  private _tryLoadBlackBackground() {
    const value = this.std
      .get(EditPropsStore)
      .getStorage('presentBlackBackground');
    // Default false - consistent with navigator-setting-button.ts
    this._blackBackground = value ?? false;
  }

  /**
   * Find the edgeless root element and cache it
   */
  private _getEdgelessRoot(): Element | null {
    if (!this._edgelessRoot) {
      this._edgelessRoot = this.host.querySelector('affine-edgeless-root');
    }
    return this._edgelessRoot;
  }

  /**
   * Apply or remove black background by toggling CSS class on edgeless root
   */
  private _updateBlackBackground(show: boolean): void {
    const root = this._getEdgelessRoot();
    if (!root) return;

    if (show) {
      root.classList.add(BLACK_BACKGROUND_CLASS);
    } else {
      root.classList.remove(BLACK_BACKGROUND_CLASS);
    }
  }

  override firstUpdated() {
    const { _disposables, gfx } = this;

    // Inject global styles for black background
    this._injectGlobalStyles();

    // Listen to local setting changes (from moderator UI)
    _disposables.add(
      this._slots.navigatorSettingUpdated.subscribe(({ blackBackground }) => {
        if (blackBackground !== undefined) {
          this.std
            .get(EditPropsStore)
            .setStorage('presentBlackBackground', blackBackground);

          this._blackBackground = blackBackground;

          const isPresentMode =
            this.gfx.tool.currentToolOption$.peek()?.toolType === PresentTool;
          this._updateBlackBackground(blackBackground && isPresentMode);
        }
      })
    );

    // Listen to settings changes from FollowerSettingsSyncService
    // (uses CustomEvent because StorageEvent only fires for OTHER tabs)
    const settingsHandler = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.blackBackground !== undefined) {
        this._blackBackground = detail.blackBackground;
        const isPresentMode =
          this.gfx.tool.currentToolName$.peek() === 'frameNavigator';
        this._updateBlackBackground(isPresentMode && this._blackBackground);
      }
    };
    window.addEventListener('presentation-settings-changed', settingsHandler);
    _disposables.add(() =>
      window.removeEventListener(
        'presentation-settings-changed',
        settingsHandler
      )
    );

    _disposables.add(
      effect(() => {
        const tool = gfx.tool.currentToolName$.value;
        const isPresentMode = tool === 'frameNavigator';
        this._updateBlackBackground(isPresentMode && this._blackBackground);
      })
    );

    this._tryLoadBlackBackground();
  }

  /**
   * Inject global CSS styles for the black background class.
   * This overrides the edgeless background when the class is applied.
   */
  private _injectGlobalStyles(): void {
    const styleId = 'presentation-black-background-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      affine-edgeless-root.${BLACK_BACKGROUND_CLASS} .edgeless-background {
        background-color: black !important;
        background-image: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  override disconnectedCallback(): void {
    // Clean up: remove black background class when widget is destroyed
    this._updateBlackBackground(false);
    super.disconnectedCallback();
  }

  override render() {
    // This widget no longer renders anything - it just manages the CSS class
    return nothing;
  }
}

export const edgelessNavigatorBgWidget = WidgetViewExtension(
  'affine:page',
  EDGELESS_NAVIGATOR_BLACK_BACKGROUND_WIDGET,
  literal`${unsafeStatic(EDGELESS_NAVIGATOR_BLACK_BACKGROUND_WIDGET)}`
);
